import React from "react";
import { MapPin, Phone, Mail, MessageCircle, Languages } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ConsultationForm from "@/components/ConsultationForm";
import { useLanguage } from "@/lib/LanguageContext";
import useSEO from "@/lib/useSEO";
import { gaPhoneClick, gaEmailClick, gaWhatsappClick } from "@/lib/analytics";
import SocialLinks from "@/components/SocialLinks";

const SERVICE_AREAS = ["Palmilla", "Villas del Mar", "Querencia", "Chileno Bay", "Maravilla", "El Dorado"];

export default function Contact() {
    const { t, lang } = useLanguage();

    useSEO({
        title: lang === "es"
            ? "Contacte Pacific Key Management | Los Cabos"
            : "Contact Pacific Key Management | Los Cabos",
        description: lang === "es"
            ? "Contacte Pacific Key Management para tratar administración de propiedades de lujo, supervisión de estates, concierge, rentas, mantenimiento o renovaciones en Los Cabos."
            : "Contact Pacific Key Management to discuss luxury property management, estate oversight, concierge services, rentals, maintenance or renovation needs in Los Cabos.",
        path: "/contact",
        keywords: "Contact Pacific Key Management, Luxury Property Management Los Cabos contact",
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
        ],
    });

    return (
        <div data-testid="contact-page">
            <PageHero
                eyebrow={t("contact.heroEyebrow")}
                title={<>{t("contact.heroTitle1")} <span className="italic font-light">{t("contact.heroTitle2")}</span></>}
                subtitle={t("contact.heroSubtitle")}
                image="https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?auto=format&fit=crop&w=2200&q=85"
                height="h-[60vh]"
            />

            <section className="bg-pearl py-24 sm:py-32">
                <div className="container-luxe grid lg:grid-cols-12 gap-14 lg:gap-20">
                    <Reveal className="lg:col-span-5 space-y-9">
                        <div>
                            <p className="overline text-gold">{t("contact.directEyebrow")}</p>
                            <h2 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight">{t("contact.directTitle")}</h2>
                            <span className="block mt-5 gold-divider" />
                        </div>

                        <div className="space-y-7 text-navy/80">
                            <div className="flex items-start gap-4">
                                <Phone className="w-5 h-5 text-gold mt-1" strokeWidth={1.25} />
                                <div>
                                    <p className="overline text-navy/55 mb-1">{t("contact.telephone")}</p>
                                    <a href="tel:+13107706585" onClick={() => gaPhoneClick({ cta_location: "contact_page" })} className="text-lg hover:text-gold transition-colors" data-testid="contact-phone">+1 (310) 770-6585</a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Mail className="w-5 h-5 text-gold mt-1" strokeWidth={1.25} />
                                <div>
                                    <p className="overline text-navy/55 mb-1">{t("contact.emailGeneral")}</p>
                                    <a href="mailto:info@pacifickeymanagement.com" onClick={() => gaEmailClick({ cta_location: "contact_page", email_type: "general" })} className="text-base hover:text-gold transition-colors break-all" data-testid="contact-email">
                                        info@pacifickeymanagement.com
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Mail className="w-5 h-5 text-gold mt-1" strokeWidth={1.25} />
                                <div>
                                    <p className="overline text-navy/55 mb-1">{t("contact.emailManagement")}</p>
                                    <a href="mailto:management@pacifickeymanagement.com" onClick={() => gaEmailClick({ cta_location: "contact_page", email_type: "management" })} className="text-base hover:text-gold transition-colors break-all" data-testid="contact-email-management">
                                        management@pacifickeymanagement.com
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <MessageCircle className="w-5 h-5 text-gold mt-1" strokeWidth={1.25} />
                                <div>
                                    <p className="overline text-navy/55 mb-1">WhatsApp</p>
                                    <a href="https://wa.me/13107706585" target="_blank" rel="noopener noreferrer" onClick={() => gaWhatsappClick({ cta_location: "contact_page" })} className="text-base hover:text-gold transition-colors" data-testid="contact-whatsapp">
                                        +1 (310) 770-6585
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <MapPin className="w-5 h-5 text-gold mt-1" strokeWidth={1.25} />
                                <div>
                                    <p className="overline text-navy/55 mb-1">{t("contact.office")}</p>
                                    <p className="text-base">{t("footer.address")}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Languages className="w-5 h-5 text-gold mt-1" strokeWidth={1.25} />
                                <div>
                                    <p className="overline text-navy/55 mb-1">{t("contact.languages")}</p>
                                    <p className="text-base">{t("contact.languagesValue")}</p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-8 border-t border-navy/10">
                            <p className="overline text-navy/55 mb-4">{t("contact.serviceAreas")}</p>
                            <ul className="grid grid-cols-2 gap-2 text-sm text-navy/75">
                                {SERVICE_AREAS.map((s) => (
                                    <li key={s} data-testid={`service-area-${s.toLowerCase().replace(/\s+/g, "-")}`}>· {s}</li>
                                ))}
                            </ul>
                        </div>

                        <SocialLinks
                            label={t("contact.followUs")}
                            labelClass="overline text-navy/55 mb-4 pt-8 border-t border-navy/10 block"
                            iconClass="w-5 h-5"
                            linkClass="text-navy/60 hover:text-gold transition-colors duration-300"
                        />
                    </Reveal>

                    <Reveal delay={140} className="lg:col-span-7 bg-sand/30 p-8 sm:p-12 border border-navy/10">
                        <p className="overline text-gold mb-3">{t("contact.formEyebrow")}</p>
                        <h3 className="font-serif text-3xl sm:text-4xl mb-8 leading-tight">{t("contact.formTitle")}</h3>
                        <ConsultationForm />
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
